import type {
  AbstractPartDefinition,
  Blueprint,
  BlueprintRenderer,
  DragConstraint,
  DragUpdateOptions,
  DragUpdateResult,
  PartRenderer,
  SnapAnchor,
} from "@project/emerge-types";
import type { ThreeEvent } from "@react-three/fiber";
import { createElement, useCallback } from "react";
import { Vector3 } from "three";
import { DraggablePart } from "../components/DraggablePart";
import { usePartMenuStore } from "../stores/partMenuStore";
import { useProductStoreContext } from "./ProductStoreContext";
import { useCameraControls } from "./ProductViewerCamera";

export const BlueprintRendererComponent = ({
  blueprint,
  renderer,
}: {
  blueprint: Blueprint;
  renderer: BlueprintRenderer | undefined;
}) => {
  const useProductStore = useProductStoreContext();
  // Subscribe to this renderer's own visibility slice so toggling unrelated
  // visibility (e.g. measurements) does not re-render the blueprint.
  const isHidden = useProductStore((state) =>
    state.hiddenPartTypes.includes(blueprint.type),
  );
  if (isHidden || !renderer) return null;
  return createElement(renderer, { blueprint, key: "blueprint" });
};

export type DragDisplayValues = DragUpdateResult<unknown>["displayValues"];

export type DragOnMoveResult = Pick<
  DragUpdateResult<unknown>,
  "displayValues" | "snappedPosition"
>;

/** Placement position: for PianoHinge use positions[0], otherwise part.position. */
function getPartPlacementPosition(
  part: AbstractPartDefinition<string>,
): Vector3 {
  if (
    part.type === "PianoHinge" &&
    "positions" in part &&
    Array.isArray((part as { positions?: Vector3[] }).positions) &&
    (part as { positions: Vector3[] }).positions.length > 0
  ) {
    const positions = (part as { positions: Vector3[] }).positions;
    const p = positions[0];
    if (p) return new Vector3(p.x, p.y, p.z);
  }
  if ("position" in part && part.position) {
    return part.position;
  }
  return new Vector3(0, 0, 0);
}

export const PartRendererComponent = ({
  part,
  renderer,
  group,
  onDragUpdate,
}: {
  part: AbstractPartDefinition<string>;
  renderer: PartRenderer | undefined;
  group: string;
  onDragUpdate?: (
    partId: string,
    position: Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
    options?: DragUpdateOptions,
  ) => DragOnMoveResult | undefined;
}) => {
  const cameraControls = useCameraControls();
  const useProductStore = useProductStoreContext();
  // Each part subscribes to only its own type's visibility, so toggling a
  // different type (or the measurement overlay) does not re-render this part.
  const isHidden = useProductStore((state) =>
    state.hiddenPartTypes.includes(part.type),
  );
  const placementPosition = getPartPlacementPosition(part);

  const handlePointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      cameraControls?.setOrbitPointFromPointer(event);
      event.stopPropagation();
    },
    [cameraControls],
  );

  if (!renderer) return null;

  const partContent = createElement(renderer, {
    id: part.id || "",
    key: `renderer-${part.id || part.type}-${group}`,
  });

  if (part.dragConstraint && onDragUpdate) {
    return (
      <group visible={!isHidden} key={`part-${part.id || part.type}-${group}`}>
        <DraggablePart
          partId={part.id}
          position={placementPosition}
          rotation={part.rotation}
          constraint={part.dragConstraint}
          debug={false}
          controlled={true}
          onDragStart={() => {
            return cameraControls?.disableControls();
          }}
          onActivate={() => {
            const optionUuid = part.dragConstraint?.sourceOptionUuid;
            if (optionUuid) {
              usePartMenuStore.getState().requestPartMenu(optionUuid);
            }
          }}
          onDrag={(newPosition: Vector3, options?: DragUpdateOptions) => {
            if (part.dragConstraint && onDragUpdate) {
              return onDragUpdate(
                part.id,
                newPosition,
                part.dragConstraint,
                part.settingsContext,
                options,
              );
            }
          }}
          onDragEnd={(
            finalPos: Vector3,
            _snappedAnchor?: SnapAnchor,
            options?: DragUpdateOptions,
          ) => {
            if (part.dragConstraint) {
              onDragUpdate(
                part.id,
                finalPos,
                part.dragConstraint,
                part.settingsContext,
                options,
              );
            }
          }}
        >
          {partContent}
        </DraggablePart>
      </group>
    );
  }

  return (
    <group
      position={[placementPosition.x, placementPosition.y, placementPosition.z]}
      rotation={[part.rotation.x, part.rotation.y, part.rotation.z]}
      visible={!isHidden}
      key={`part-${part.id || part.type}-${group}`}
      onPointerDown={handlePointerDown}
      onPointerEnter={(e) => e.stopPropagation()}
      onPointerLeave={(e) => e.stopPropagation()}
    >
      {partContent}
    </group>
  );
};
